import React, { useState, useEffect } from 'react';
import { Star, StarHalf, MessageCircle, ThumbsUp, Filter, RefreshCw, AlertTriangle, Check, X } from 'lucide-react';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { reviewService, Review } from '../../services/reviewService';
import { useLanguage } from '../../hooks/useLanguage';

export const Reviews: React.FC = () => {
    const { t, isRTL } = useLanguage();
    const [reviews, setReviews] = useState<Review[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedReview, setSelectedReview] = useState<Review | null>(null);
    const [responseText, setResponseText] = useState('');
    const [filter, setFilter] = useState('all');
    const [stats, setStats] = useState({
        averageRating: 0,
        totalReviews: 0
    });
    const [saving, setSaving] = useState(false);
    const [totalPages, setTotalPages] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);

    // Fonction pour charger les avis du garage
    const loadReviews = async () => {
        try {
            setLoading(true);
            setError(null);

            // Récupérer l'ID du garage depuis localStorage
            const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';
            if (!garageId) {
                throw new Error(t('reviews.errors.garageIdNotFound'));
            }

            // Appel au service d'avis
            const response = await reviewService.getGarageReviews(garageId, {
                status: filter !== 'all' ? filter : undefined,
                page: currentPage,
                limit: 5,
                sort: 'recent'
            });

            setReviews(response.reviews);
            setStats(response.stats);
            setTotalPages(response.pagination.pages);

        } catch (err) {
            console.error('Erreur:', err);
            setError(err instanceof Error ? err.message : t('reviews.errors.general'));
        } finally {
            setLoading(false);
        }
    };

    // Charger les avis au chargement du composant et lorsque le filtre ou la page change
    useEffect(() => {
        loadReviews();
    }, [filter, currentPage]);

    // Convertir une note en étoiles visuelles
    const renderStars = (rating: number) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<Star key={`full-${i}`} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
        }

        if (hasHalfStar) {
            stars.push(<StarHalf key="half" className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
        }

        const remainingStars = 5 - stars.length;
        for (let i = 0; i < remainingStars; i++) {
            stars.push(<Star key={`empty-${i}`} className="w-4 h-4 text-gray-300" />);
        }

        return stars;
    };

    // Traduire les catégories
    const translateCategory = (category: string) => {
        const translations: Record<string, string> = {
            'ACCUEIL': t('reviews.categories.accueil'),
            'RAPIDITE': t('reviews.categories.rapidite'),
            'QUALITE': t('reviews.categories.qualite'),
            'PRIX': t('reviews.categories.prix'),
            'COMMUNICATION': t('reviews.categories.communication'),
            'PONCTUALITE': t('reviews.categories.ponctualite'),
            'PROPRETE': t('reviews.categories.proprete'),
            'SERVICE_CLIENT': t('reviews.categories.serviceClient'),
            'EXPERTISE': t('reviews.categories.expertise')
        };
        return translations[category] || category;
    };

    // Répondre à un avis
    const respondToReview = async () => {
        if (!selectedReview || !responseText.trim()) return;

        try {
            setSaving(true);

            // Récupérer l'ID du garage depuis localStorage
            const garageId = JSON.parse(localStorage.getItem('user') || '{}')._id || '';

            // Appel au service pour répondre à l'avis
            await reviewService.respondToReview(selectedReview._id, garageId, responseText);

            // Mettre à jour la liste des avis
            await loadReviews();

            // Fermer la modal et réinitialiser le formulaire
            setSelectedReview(null);
            setResponseText('');

        } catch (err) {
            console.error('Erreur:', err);
            setError(err instanceof Error ? err.message : t('reviews.errors.general'));
        } finally {
            setSaving(false);
        }
    };

    // Formater la date
    const formatDate = (date: string | Date) => {
        return format(new Date(date), 'dd MMMM yyyy', { locale: isRTL ? ar : undefined });
    };

    // Pagination
    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    return (
        <div className="space-y-6" dir={isRTL ? 'rtl' : 'ltr'}>
            {/* En-tête avec statistiques */}
            <div className={`flex flex-col md:flex-row justify-between items-start md:items-center mb-6 ${isRTL ? 'md:flex-row-reverse' : ''}`}>
                <div className={isRTL ? 'text-right' : 'text-left'}>
                    <h1 className="text-2xl font-bold text-gray-800">{t('reviews.title')}</h1>
                    <p className="text-gray-600">{t('reviews.subtitle')}</p>
                </div>

                <div className={`flex items-center space-x-4 mt-4 md:mt-0 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                    <div className={`flex items-center bg-white px-3 py-2 rounded-lg shadow-sm ${isRTL ? 'flex-row-reverse space-x-reverse' : 'space-x-1'}`}>
                        {renderStars(stats.averageRating)}
                        <span className={`font-semibold ${isRTL ? 'mr-2' : 'ml-2'}`}>{stats.averageRating.toFixed(1)}</span>
                        <span className={`text-gray-500 text-sm ${isRTL ? 'mr-1' : 'ml-1'}`}>({stats.totalReviews} {t('reviews.stats.reviews')})</span>
                    </div>

                    <div className="flex items-center">
                        <select
                            value={filter}
                            onChange={(e) => setFilter(e.target.value)}
                            className={`bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isRTL ? 'text-right' : ''}`}
                        >
                            <option value="all">{t('reviews.filters.all')}</option>
                            <option value="approved">{t('reviews.filters.approved')}</option>
                            <option value="pending">{t('reviews.filters.pending')}</option>
                            <option value="reported">{t('reviews.filters.reported')}</option>
                        </select>
                        <button
                            onClick={() => loadReviews()}
                            className={`p-2 rounded-md hover:bg-gray-100 ${isRTL ? 'mr-2' : 'ml-2'}`}
                            title={t('reviews.actions.refresh')}
                        >
                            <RefreshCw className="h-5 w-5 text-gray-500" />
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className={`bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg ${isRTL ? 'text-right' : ''}`}>
                    {error}
                </div>
            )}

            {/* Liste des avis */}
            {loading ? (
                <div className="flex justify-center py-20">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : reviews.length === 0 ? (
                <div className={`bg-white rounded-lg shadow p-6 text-center ${isRTL ? 'text-right' : ''}`}>
                    <p className="text-gray-500">{t('reviews.noReviews')}</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {reviews.map((review) => (
                        <div key={review._id} className="bg-white rounded-lg shadow-lg p-6">
                            {/* En-tête de l'avis */}
                            <div className={`flex justify-between items-start ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <div className={isRTL ? 'text-right' : ''}>
                                    <div className={`flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}>
                                        <h3 className="text-lg font-semibold">{review.clientName}</h3>
                                        {review.isVerified && (
                                            <span className={`px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full ${isRTL ? 'mr-2' : 'ml-2'}`}>
                                                {t('reviews.verified')}
                                            </span>
                                        )}
                                        <span className={`text-gray-500 text-sm ${isRTL ? 'mr-2' : 'ml-2'}`}>
                                            • {formatDate(review.createdAt)}
                                        </span>
                                    </div>
                                    <div className={`flex items-center mt-1 ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
                                        {renderStars(review.rating)}
                                    </div>
                                </div>

                                <div>
                                    {review.status === 'PENDING' && (
                                        <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
                                            {t('reviews.status.pending')}
                                        </span>
                                    )}
                                    {review.status === 'APPROVED' && (
                                        <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm">
                                            {t('reviews.status.approved')}
                                        </span>
                                    )}
                                    {review.status === 'REPORTED' && (
                                        <span className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm">
                                            {t('reviews.status.reported')}
                                        </span>
                                    )}
                                    {review.status === 'REJECTED' && (
                                        <span className="px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm">
                                            {t('reviews.status.rejected')}
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Titre et contenu */}
                            <h4 className={`font-medium text-gray-900 mt-3 ${isRTL ? 'text-right' : ''}`}>{review.title}</h4>
                            <p className={`text-gray-700 mt-2 ${isRTL ? 'text-right' : ''}`}>{review.content}</p>

                            {/* Catégories */}
                            {review.categories && review.categories.length > 0 && (
                                <div className={`flex flex-wrap gap-2 mt-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                    {review.categories.map(category => (
                                        <span key={category} className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs">
                                            {translateCategory(category)}
                                        </span>
                                    ))}
                                </div>
                            )}

                            {/* Réponse existante */}
                           {/* {review.response && (
                                <div className="mt-4 bg-gray-50 p-4 rounded-lg border-l-4 border-indigo-500">
                                    <p className="text-sm font-medium text-gray-500">Votre réponse • {formatDate(review.response.updatedAt)}</p>
                                    <p className="mt-1">{review.response.content}</p>
                                </div>
                            )}

                             Actions
                            <div className="mt-4 flex justify-between items-center pt-3 border-t border-gray-200">
                                <div className="flex items-center text-gray-500 text-sm">
                                    <ThumbsUp className="h-4 w-4 mr-1" />
                                    <span>{review.helpfulCount} personnes ont trouvé cet avis utile</span>
                                </div>

                                {!review.response && (
                                    <button
                                        onClick={() => {
                                            setSelectedReview(review);
                                            setResponseText('');
                                        }}
                                        className="flex items-center text-indigo-600 hover:text-indigo-800"
                                    >
                                        <MessageCircle className="h-5 w-5 mr-1" />
                                        Répondre
                                    </button>
                                )}

                                {review.response && (
                                    <button
                                        onClick={() => {
                                            setSelectedReview(review);
                                            setResponseText(review.response?.content || '');
                                        }}
                                        className="flex items-center text-gray-600 hover:text-gray-800"
                                    >
                                        <MessageCircle className="h-5 w-5 mr-1" />
                                        Modifier la réponse
                                    </button>
                                )}
                            </div>*/}
                        </div>
                    ))}

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex justify-center mt-6">
                            <nav className={`flex items-center space-x-2 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                                <button
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                    className="px-3 py-1 rounded border bg-white text-gray-700 disabled:opacity-50"
                                >
                                    {isRTL ? t('reviews.pagination.next') : t('reviews.pagination.previous')}
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                    <button
                                        key={page}
                                        onClick={() => handlePageChange(page)}
                                        className={`px-3 py-1 rounded ${
                                            currentPage === page
                                                ? 'bg-indigo-600 text-white'
                                                : 'bg-white text-gray-700 border'
                                        }`}
                                    >
                                        {page}
                                    </button>
                                ))}

                                <button
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                    className="px-3 py-1 rounded border bg-white text-gray-700 disabled:opacity-50"
                                >
                                    {isRTL ? t('reviews.pagination.previous') : t('reviews.pagination.next')}
                                </button>
                            </nav>
                        </div>
                    )}
                </div>
            )}

            {/* Modal de réponse */}
            {selectedReview && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-xl shadow-lg max-w-3xl w-full m-4 p-6" dir={isRTL ? 'rtl' : 'ltr'}>
                        <div className={`flex justify-between items-center mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                            <h3 className="text-xl font-bold text-gray-900">
                                {selectedReview.response ? t('reviews.modal.editResponse') : t('reviews.modal.replyToReview')}
                            </h3>
                            <button
                                onClick={() => setSelectedReview(null)}
                                className="p-1 rounded-full hover:bg-gray-100"
                            >
                                <X className="w-6 h-6 text-gray-500" />
                            </button>
                        </div>

                        {/* Détails de l'avis */}
                        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                            <div className={`flex items-center mb-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                                <h4 className="font-medium">{selectedReview.clientName}</h4>
                                <div className={`flex ${isRTL ? 'mr-2' : 'ml-2'}`}>
                                    {renderStars(selectedReview.rating)}
                                </div>
                            </div>
                            <p className={`text-gray-700 ${isRTL ? 'text-right' : ''}`}>{selectedReview.content}</p>
                        </div>

                        {/* Formulaire de réponse */}
                        <div className="mb-4">
                            <label className={`block text-sm font-medium text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                                {t('reviews.modal.yourResponse')}
                            </label>
                            <textarea
                                value={responseText}
                                onChange={(e) => setResponseText(e.target.value)}
                                className={`w-full border border-gray-300 rounded-lg p-3 min-h-[120px] focus:ring-indigo-500 focus:border-indigo-500 ${isRTL ? 'text-right' : ''}`}
                                placeholder={t('reviews.modal.responsePlaceholder')}
                                dir={isRTL ? 'rtl' : 'ltr'}
                            ></textarea>
                        </div>

                        <div className={`flex justify-end space-x-3 ${isRTL ? 'flex-row-reverse space-x-reverse' : ''}`}>
                            <button
                                onClick={() => setSelectedReview(null)}
                                className="px-4 py-2 border border-gray-300 rounded-lg shadow-sm text-gray-700 bg-white hover:bg-gray-50"
                            >
                                {t('reviews.modal.cancel')}
                            </button>
                            <button
                                onClick={respondToReview}
                                disabled={saving || !responseText.trim()}
                                className={`px-4 py-2 border border-transparent rounded-lg shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 flex items-center ${isRTL ? 'flex-row-reverse' : ''}`}
                            >
                                {saving ? (
                                    <>
                                        <RefreshCw className={`w-4 h-4 animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`} />
                                        {t('reviews.modal.saving')}
                                    </>
                                ) : (
                                    selectedReview.response ? t('reviews.modal.update') : t('reviews.modal.sendResponse')
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};